import { NO_ESTIMATE_FILTER_ID } from 'containers/jobs/constant';

export enum EstimateStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
  ARCHIVED = 'ARCHIVED',
  INVOICED = 'INVOICED'
}

export enum EstimateStatusTitles {
  PENDING = 'Pending',
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
  EXPIRED = 'Expired',
  ARCHIVED = 'Archived',
  INVOICED = 'Invoiced'
}

export enum EstimatePageState {
  CREATE = 'CREATE',
  PREVIEW = 'PREVIEW'
}

export const EstimatesStatuses = Object.keys(EstimateStatus).map(key => ({
  value: EstimateStatus[key as keyof typeof EstimateStatus],
  label: EstimateStatusTitles[key as keyof typeof EstimateStatusTitles]
}));

export const EstimateStatusesForFilter = [{ value: NO_ESTIMATE_FILTER_ID, label: 'No estimate' }, ...EstimatesStatuses];

export enum CreateEstimateErrorTypes {
  ESTIMATE_TITLE = 'estimateTitle',
  CUSTOMER = 'customer',
  SERVICE_ADDRESS = 'serviceAddress',
  LINE_ITEMS = 'lineItems'
}

export const createEstimateErrors = {
  [CreateEstimateErrorTypes.ESTIMATE_TITLE]: 'Estimate title cannot be empty',
  [CreateEstimateErrorTypes.CUSTOMER]: 'Customer cannot be empty',
  [CreateEstimateErrorTypes.SERVICE_ADDRESS]: 'Service address cannot be empty',
  [CreateEstimateErrorTypes.LINE_ITEMS]: 'At least one line item is required'
};

export enum CreateEditEstimateNavEnum {
  CUSTOMER = 'Customer',
  ESTIMATE_DETAILS = 'Estimate details',
  LINE_ITEMS = 'Line items',
  NOTES_ATTACHMENTS = 'Notes & Attachments',
  MORE_OPTIONS = 'More options'
}

export const estimateSearchTypes = [
  {
    id: 'search-option-estimate-title',
    text: 'Title',
    searchText: 'Search with estimate title',
    type: 'title'
  },
  {
    id: 'search-option-customer',
    text: 'Customer',
    searchText: 'Search with customer',
    type: 'customer'
  },
  {
    id: 'search-option-estimate-title',
    text: 'Reference ID',
    searchText: 'Search with estimate reference ID',
    type: 'referenceId'
  },
  {
    id: 'search-option-estimate-title',
    text: 'Line items',
    searchText: 'Search with estimate line items',
    type: 'lineItems'
  },
  {
    id: 'search-option-invoice-id',
    text: 'Invoice ID',
    searchText: 'Search with invoice number',
    type: 'invoices'
  },
  {
    id: 'search-option-job-id',
    text: 'Job ID',
    searchText: 'Search with job number',
    type: 'jobs'
  },
  {
    id: 'search-option-estimate-title',
    text: 'Estimate ID',
    searchText: 'Search with estimate number',
    type: 'estimates'
  }
];

export enum ESTIMATE_TAB_TITLE {
  ALL_ESTIMATES = 'All estimates',
  PENDING = 'Pending',
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
  INVOICED = 'Invoiced'
}

export const estimateTabs = [
  {
    id: 1,
    title: ESTIMATE_TAB_TITLE.PENDING
  },
  {
    id: 2,
    title: ESTIMATE_TAB_TITLE.ACCEPTED
  },
  {
    id: 3,
    title: ESTIMATE_TAB_TITLE.ALL_ESTIMATES
  },
  {
    id: 4,
    title: ESTIMATE_TAB_TITLE.INVOICED
  },
  {
    id: 5,
    title: ESTIMATE_TAB_TITLE.REJECTED
  }
];

export const defaultEstimateStatusCount = {
  [ESTIMATE_TAB_TITLE.ALL_ESTIMATES]: 0,
  [ESTIMATE_TAB_TITLE.PENDING]: 0,
  [ESTIMATE_TAB_TITLE.ACCEPTED]: 0,
  [ESTIMATE_TAB_TITLE.REJECTED]: 0,
  [ESTIMATE_TAB_TITLE.INVOICED]: 0
};

export enum ESTIMATE_ATTRIBUTE_TYPE {
  REFERENCE_NUMBER = 'referenceNo'
}
export enum CreateEstimateButtonIds {
  CREATE_BUTTON = 'create-edit-estimate-save-button',
  CREATE_BACK_BUTTON = 'create-edit-estimate-back-button'
}

export const CURRENT_ESTIMATE_FEATURES = [
  'Create and send unlimited estimates',
  'Customise branding',
  'Link jobs and invoices to estimates',
  'Assign estimates to your team'
];

export const UPCOMING_ESTIMATE_FEATURES = [
  'Create custom templates',
  'Auto-convert accepted estimates to invoices',
  'Collect deposits and set up payment schedules'
];
export const STATUS_CHANGE_ALLOWED_STATUSES = Object.keys(EstimateStatus).map(
  key => EstimateStatus[key as keyof typeof EstimateStatus]
);

export enum EstimateMoreActions {
  SHARE = 'share',
  ARCHIVE = 'archive',
  DUPLICATE = 'duplicate',
  VIEW_INVOICE = 'viewInvoice',
  VIEW_JOB = 'viewJob',
  ADD_JOB = 'addJob',
  ADD_INVOICE = 'addInvoice',
  MARK_AS_ACCEPTED = 'markAsAccepted',
  CHANGE_STATUS = 'changeStatus',
  DELETE = 'delete',
  ADD_NOTE = 'addNote'
}

export enum SubscriptionProcessingStatus {
  PURCHASED = 'purchased',
  ERROR = 'error',
  CANCELED = 'canceled',
  INITIATED = 'initiated'
}

export enum SubscriptionPlanType {
  FREE = 'free',
  PAID = 'paid'
}

export enum SubscriptionFrequency {
  ONE_TIME = 'one-time',
  MONTH = 'month'
}

export enum SubscriptionDescription {
  FREE = 'free',
  ESTIMATE_ONLY = 'estimate_only'
}

export enum SubscriptionPlanName {
  TEST_EST_MONTHLY_PLAN_WEB = 'test_est_monthly_plan_web',
  TEST_EST_MONTHLY_PLAN = 'test_est_monthly_plan'
}

export enum CheckoutSessionStatus {
  COMPLETE = 'complete',
  OPEN = 'open'
}

export enum UnlinkSourceType {
  JOB = 'job',
  INVOICE = 'invoice',
  ESTIMATE = 'estimate'
}

export const EXPIRY_PERIOD_OPTIONS: ExpiryPeriodOption[] = [
  { id: 'no-expiry', name: 'No expiry', days: 0, btnLabel: 'No expiry' },
  { id: '7-days', name: '7 days from issue date', days: 7, btnLabel: '7 days' },
  { id: '14-days', name: '14 days from issue date', days: 14, btnLabel: '14 days' },
  { id: '30-days', name: '30 days from issue date', days: 30, btnLabel: '30 days' },
  { id: '45-days', name: '45 days from issue date', days: 45, btnLabel: '45 days' },
  { id: '60-days', name: '60 days from issue date', days: 60, btnLabel: '60 days' },
  { id: '90-days', name: '90 days from issue date', days: 90, btnLabel: '90 days' }
];

export interface ExpiryPeriodOption {
  id: string;
  name: string;
  days: number;
  btnLabel: string;
}
